import React, { useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Chip,
	Divider,
	IconButton,
	ListSubheader,
	Menu,
	MenuItem,
	Stack,
	Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useDispatch } from 'react-redux';
import { isModifiedClick } from '@/utils/navigation';
import { openDialog } from '@/features/dialog/dialogSlice';
import NotificationMenuItem from '@/components/layouts/NotificationsMenu/NotificationMenuItem';
import {
	useListEventNotificationsQuery,
	useMarkAllEventNotificationsReadMutation,
	useUpdateEventNotificationMutation,
} from '@/features/notifications/notificationsApi';

const FILTERS = [
	{ id: 'unread', label: 'Unread' },
	{ id: 'read', label: 'Read' },
	{ id: 'archived', label: 'Archived' },
];

const NotificationsMenu = () => {
	const dispatch = useDispatch();
	const [anchorEl, setAnchorEl] = useState(null);
	const [activeFilter, setActiveFilter] = useState('unread');
	const open = Boolean(anchorEl);

	const { data, isFetching } = useListEventNotificationsQuery(
		{ status: activeFilter },
		{ pollingInterval: 60_000 },
	);
	const { data: unreadData } = useListEventNotificationsQuery(
		{ status: 'unread' },
		{ pollingInterval: 60_000 },
	);

	const [updateNotification] = useUpdateEventNotificationMutation();
	const [markAllRead, markAllReadState] = useMarkAllEventNotificationsReadMutation();

	const notifications = data?.notifications ?? [];
	const unreadCount = unreadData?.unreadCount ?? 0;

	const hasUnread = unreadCount > 0;
	const isBulkLoading = markAllReadState.isLoading;

	const handleOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleNotificationClick = (event, notification) => {
		if (event.button === 2) {
			return;
		}
		handleClose();
		if (isModifiedClick(event)) {
			return;
		}
		// A Folio AI email draft opens in the compose drawer on its deal or client's page
		if (notification.metadata?.draftId) {
			dispatch(
				openDialog({
					dialogName: 'unlayerEmail',
					data: {
						clientLocked: true,
						dealLocked: Boolean(notification.metadata.dealId),
						draftId: notification.metadata.draftId,
						draftClientId: notification.metadata.clientId,
						draftDealId: notification.metadata.dealId,
					},
				}),
			);
		}
		if (notification.status !== 'unread') {
			return;
		}
		updateNotification({
			notificationId: notification.id,
			status: 'read',
		})
			.unwrap()
			.catch((error) => {
				console.error('Error marking notification as read', error);
			});
	};

	const handleArchive = async (event, notificationId) => {
		event.preventDefault();
		event.stopPropagation();
		try {
			await updateNotification({ notificationId, status: 'archived' }).unwrap();
		} catch (error) {
			console.error('Error archiving notification', error);
		}
	};

	const handleMarkAllRead = async () => {
		try {
			await markAllRead().unwrap();
		} catch (error) {
			console.error('Error marking all notifications as read', error);
		}
	};

	const emptyMessage = useMemo(() => {
		switch (activeFilter) {
			case 'read':
				return 'No read notifications';
			case 'archived':
				return 'No archived notifications';
			default:
				return 'No unread notifications';
		}
	}, [activeFilter]);

	return (
		<>
			<IconButton
				size='small'
				onClick={handleOpen}
				aria-controls='notifications-menu-appbar'
				aria-haspopup='true'
			>
				<Badge color='error' badgeContent={unreadCount} max={99} invisible={!hasUnread}>
					<NotificationsIcon color='primary' />
				</Badge>
			</IconButton>
			<Menu
				id='notifications-menu-appbar'
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				slotProps={{
					paper: {
						sx: {
							width: 380,
							maxWidth: '95vw',
							maxHeight: 480,
						},
					},
				}}
			>
				<ListSubheader sx={{ fontSize: '14px', lineHeight: '32px', px: 2 }}>
					<Stack direction='row' alignItems='center' justifyContent='space-between'>
						<Typography variant='details_header' fontSize='14px'>
							Notifications
						</Typography>
						<Stack direction='row' spacing={1}>
							<Button
								size='small'
								disabled={!hasUnread || isBulkLoading}
								onClick={handleMarkAllRead}
							>
								Mark all read
							</Button>
						</Stack>
					</Stack>
				</ListSubheader>
				<Box px={2} pb={1}>
					<Stack direction='row' spacing={1}>
						{FILTERS.map((filter) => (
							<Chip
								key={filter.id}
								label={filter.label}
								size='small'
								color={activeFilter === filter.id ? 'primary' : 'default'}
								variant={activeFilter === filter.id ? 'filled' : 'outlined'}
								onClick={() => setActiveFilter(filter.id)}
							/>
						))}
					</Stack>
				</Box>
				<Divider />
				{isFetching && notifications.length === 0 ? (
					<MenuItem disabled>
						<Typography variant='task_updated'>Loading...</Typography>
					</MenuItem>
				) : notifications.length === 0 ? (
					<MenuItem disabled>
						<Typography variant='task_updated'>{emptyMessage}</Typography>
					</MenuItem>
				) : (
					notifications.map((notification, index) => (
						<NotificationMenuItem
							key={notification.id}
							notification={notification}
							divider={index < notifications.length - 1}
							onClick={(event) => handleNotificationClick(event, notification)}
							onArchive={(event) => handleArchive(event, notification.id)}
						/>
					))
				)}
			</Menu>
		</>
	);
};

export default NotificationsMenu;
